/**
 * Client-side travel-cost matrix for cartesian (x-y) models.
 *
 * Cartesian models never call the backend `/cost_matrix` endpoint — its
 * methods (driving via OpenRouteService, haversine, geographic manhattan)
 * are all latitude/longitude-based. Instead the matrix is computed locally
 * on the plane and returned in the exact `CostMatrixResponse` shape, so the
 * `fetchTravelCosts` reducer, the Calculate button, and the loading states
 * all work unchanged.
 *
 * Coordinate convention: x is stored in the `longitude` field and y in
 * `latitude` (GeoJSON coordinate order is [x, y]); only the display headers
 * differ per model.
 *
 * Store-free by design (like tripPairs.js) so it stays unit-testable.
 */
import { durationFromDistance } from 'store/functions/costMethods.js';

/** Metrics supported on the plane. `driving`/`geo` (geographic-only methods)
 *  are mapped to `euclidean` defensively, so a stale persisted setting can
 *  never error out or silently call the backend. */
export function planarDistance(metric, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (metric === 'manhattan') return Math.abs(dx) + Math.abs(dy);
  return Math.hypot(dx, dy);
}

const toFiniteNumber = (value) => {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : null;
};

/**
 * Compute the full arc list for a set of locations.
 *
 * Every ordered pair is emitted, INCLUDING zero-cost self-pairs — the solver
 * treats a missing arc as unreachable (ROUTING_FAIL), so completeness is a
 * correctness requirement, not an optimization.
 *
 * Durations: when the scenario has a travel model (averageSpeed and/or
 * roadCircuityFactor, as x-y examples publish), a duration is derived from the
 * distance exactly as the backend derives it for its x-y methods:
 * distance × circuity ÷ speed × 60, with 30 and 1.25 for a missing half. Without
 * one, duration equals distance (unit speed), as before: the plane is unitless.
 *
 * @param {Object} options
 * @param {Array<Object>} options.locations - Location rows; reads `UUID`,
 *   `locationName`, and x/y from `longitude`/`latitude`.
 * @param {'euclidean'|'manhattan'} [options.metric='euclidean']
 * @param {'distance'|'duration'|string} [options.costMetric='distance'] -
 *   Which value feeds the `cost` field (mirrors generalSettings.costs.costMetric).
 * @param {number} [options.averageSpeed] - generalSettings.costs.averageSpeed
 * @param {number} [options.roadCircuityFactor] - generalSettings.costs.roadCircuityFactor
 * @returns {{travelCostResponses: Array<Object>, TravelCostErrors: Array<Object>}}
 *   The same shape the backend `/cost_matrix` endpoint returns.
 */
export function computePlanarCostMatrix({ locations, metric = 'euclidean', costMetric = 'distance', averageSpeed, roadCircuityFactor }) {
  const model = { averageSpeed, roadCircuityFactor };
  const travelCostResponses = [];
  const errors = [];

  const usable = [];
  for (const loc of locations || []) {
    const x = toFiniteNumber(loc?.longitude);
    const y = toFiniteNumber(loc?.latitude);
    if (x === null || y === null) {
      errors.push({
        errorDescription: `Location "${loc?.locationName ?? loc?.UUID ?? 'unknown'}" is missing numeric X/Y coordinates and was excluded from the travel cost matrix.`,
      });
      continue;
    }
    usable.push({ id: loc.UUID, name: loc.locationName, x, y });
  }

  for (const from of usable) {
    for (const to of usable) {
      const distance = from.id === to.id ? 0 : planarDistance(metric, from.x, from.y, to.x, to.y);
      const derived = durationFromDistance(distance, model);
      const duration = derived === null ? distance : derived;
      travelCostResponses.push({
        fromLocationId: from.id,
        toLocationId: to.id,
        fromLocationName: from.name,
        toLocationName: to.name,
        cost: costMetric === 'duration' ? duration : distance,
        distance,
        duration,
        vehicleType: null,
        vehicleTypeId: null,
        status: 's',
      });
    }
  }

  return { travelCostResponses, TravelCostErrors: errors };
}
